import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Truck } from "lucide-react";

// Narrates the filing flow, in order, as the truck crosses the strip - Add
// trucks -> tax gets calculated -> Schedule 1 comes back -> go file. Kept to
// the same beats the rest of the page already walks through (HOW_IT_WORKS /
// FEATURES in Home.jsx), just compressed into four words each.
const CAPTIONS = [
  { until: 0.3, text: "Add your trucks" },
  { until: 0.6, text: "We calculate the tax" },
  { until: 0.88, text: "Get your stamped Schedule 1" },
];

// Decorative "truck driving down the road" divider (Landing page only).
// The truck's horizontal position - and which caption is showing above it -
// tracks how far this strip has scrolled through the viewport: 0% as it
// enters the bottom of the screen, 100% as it exits the top. So it reads as
// "the truck (and the story) advances as you scroll" rather than a
// self-looping animation. Once the truck is nearly across, the caption
// becomes the same "Start Filing" CTA used elsewhere on the page.
//
// Horizontal position is driven via `left` (percent, computed in JS); the
// gentle vertical bob is layered on with Tailwind's animate-float-slow,
// which keyframes `transform` - kept on the truck element only because
// nothing else on it also needs `transform`, so the two motions never
// collide over the same CSS property.
const RoadTruck = () => {
  const trackRef = useRef(null);
  const truckRef = useRef(null);
  const frameRef = useRef(null);
  const [captionIndex, setCaptionIndex] = useState(0);
  const captionIndexRef = useRef(0);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const update = () => {
      const rect = track.getBoundingClientRect();
      const vh = window.innerHeight;
      const total = rect.height + vh;
      const progress = Math.min(1, Math.max(0, (vh - rect.top) / total));
      if (truckRef.current) {
        truckRef.current.style.left = `${(4 + progress * 88).toFixed(2)}%`;
      }
      const nextIndex = CAPTIONS.findIndex((c) => progress <= c.until);
      const resolvedIndex = nextIndex === -1 ? CAPTIONS.length : nextIndex;
      if (resolvedIndex !== captionIndexRef.current) {
        captionIndexRef.current = resolvedIndex;
        setCaptionIndex(resolvedIndex);
      }
    };

    const onScroll = () => {
      if (frameRef.current) return;
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null;
        update();
      });
    };

    let observing = false;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !observing) {
          observing = true;
          window.addEventListener("scroll", onScroll, { passive: true });
          update();
        } else if (!entry.isIntersecting && observing) {
          observing = false;
          window.removeEventListener("scroll", onScroll);
        }
      },
      { threshold: 0 }
    );
    io.observe(track);
    update();

    return () => {
      io.disconnect();
      window.removeEventListener("scroll", onScroll);
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, []);

  const isCta = captionIndex >= CAPTIONS.length;

  return (
    <div ref={trackRef} className="relative h-[78px] max-w-[1180px] mx-auto px-[22px]">
      <div className="absolute inset-x-0 top-0 h-[34px] flex items-center justify-center">
        {isCta ? (
          <Link
            key="cta"
            to="/login?tab=up"
            className="animate-fade inline-flex items-center gap-1.5 text-[15px] font-bold text-[var(--land-primary)] no-underline hover:gap-2.5 transition-[gap]"
          >
            Start Filing <ArrowRight size={16} />
          </Link>
        ) : (
          <p key={captionIndex} className="animate-fade text-[14px] font-semibold text-[var(--land-muted)]">
            {CAPTIONS[captionIndex].text}
          </p>
        )}
      </div>

      {/* road surface + dashed center line */}
      <div
        className="absolute left-[22px] right-[22px] top-[52px] h-[6px] rounded-full bg-[color-mix(in_srgb,var(--land-ink)_16%,transparent)] overflow-hidden"
        aria-hidden="true"
      >
        <div className="absolute inset-0 bg-[repeating-linear-gradient(90deg,var(--land-panel)_0_18px,transparent_18px_36px)] opacity-70" />
      </div>

      <div
        ref={truckRef}
        className="absolute animate-float-slow"
        style={{ left: "4%", top: "38px", marginLeft: "-17px" }}
        aria-hidden="true"
      >
        {/* trailing motion dashes read as "direction of travel" */}
        <div className="absolute right-full top-1/2 -translate-y-1/2 flex items-center gap-1 mr-1.5 opacity-40">
          <span className="w-2.5 h-[2px] rounded-full bg-[var(--land-muted)]" />
          <span className="w-1.5 h-[2px] rounded-full bg-[var(--land-muted)]" />
        </div>
        <Truck
          size={34}
          strokeWidth={2.25}
          className="text-[var(--land-primary)] drop-shadow-[0_6px_8px_rgba(0,0,0,.18)]"
        />
      </div>
    </div>
  );
};

export default RoadTruck;
